import React from "react";
import { createPortal } from "react-dom";
import { Camera, Video, Mic, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMAGE_PATH } from "../../api/apiConfig";

/**
 * Capture Method Selector Component
 * Shows options for capturing or uploading media before opening camera.
 * Can render as a full-screen modal (default) or inline for embedding in other UIs.
 *
 * @param {Object} props
 * @param {Function} props.onSelect - Callback when method is selected (receives mode: 'photo'|'video'|'audio'|'upload')
 * @param {Function} [props.onClose] - Callback when selector is closed (modal variant only)
 * @param {string} [props.postType] - Type of post being created ('glow', 'grateful', 'pride')
 * @param {boolean} [props.isContest] - Whether this is for a contest submission
 * @param {boolean} [props.allowClose] - Whether the user can close the selector (default: true, modal only)
 * @param {('modal'|'inline')} [props.variant] - 'modal' = full-screen overlay; 'inline' = embeddable block (default: 'modal')
 * @param {string|React.ReactNode} [props.title] - Custom prompt text (e.g. "Show us what you did:"). When set, overrides postType/isContest title.
 * @param {Array<'photo'|'video'|'audio'|'upload'>} [props.allowedOptions] - Limit to these options only (e.g. ['photo'], ['video', 'upload']). Omit for all.
 * @param {boolean} [props.darkBackground] - When true (e.g. inline on dark modal), use light text for title and option labels.
 */
const CaptureMethodSelector = ({
	onSelect,
	onClose,
	postType = "glow",
	isContest = false,
	allowClose = true,
	variant = "modal",
	title: customTitle,
	allowedOptions,
	darkBackground = false,
}) => {
	// Determine title and colors based on post type (used when customTitle not provided)
	const getTitleConfig = () => {
		// Contest submissions get special wording
		if (isContest) {
			return {
				text: (
					<>
						How would you like to
						<br />
						submit for this contest?
					</>
				),
				gradient: "from-yellow-400 to-orange-500",
			};
		}

		switch (postType) {
			case "grateful":
				return {
					text: (
						<>
							How would you like to
							<br />
							share your gratitude?
						</>
					),
					gradient: "from-green-500 to-green-600",
				};
			case "glow":
			default:
				return {
					text: (
						<>
							How would you like to
							<br />
							Capture your Glow?
						</>
					),
					gradient: "from-orange-400 to-yellow-400",
				};
		}
	};

	const titleConfig = getTitleConfig();

	// Resolve display title: custom prop overrides default
	const displayTitle =
		customTitle !== undefined && customTitle !== null
			? customTitle
			: titleConfig.text;

	// Get text and stroke colors based on post type (only used in modal variant with default title)
	const getTitleColors = () => {
		if (isContest) {
			return {
				textColor: "text-blue-500", // blue letters
				strokeColor: "rgb(37, 99, 235)", // blue-600 stroke (darker than text)
			};
		}
		if (postType === "grateful") {
			return {
				textColor: "text-green-500", // green letters
				strokeColor: "rgb(22, 163, 74)", // green-600 stroke (darker than text)
			};
		}
		// Default: glow
		return {
			textColor: "text-yellow-300", // yellow letters
			strokeColor: "rgb(251, 146, 60)", // orange-500 stroke
		};
	};

	const titleColors = getTitleColors();

	const allOptions = [
		{
			id: "photo",
			label: "Take a Photo",
			icon: Camera,
			bgColor: "bg-purple-200",
			hoverColor: "hover:bg-purple-300",
		},
		{
			id: "video",
			label: "Take a video",
			icon: Video,
			bgColor: "bg-cyan-200",
			hoverColor: "hover:bg-cyan-300",
		},
		{
			id: "audio",
			label: "Tell us what you did",
			icon: Mic,
			bgColor: "bg-pink-200",
			hoverColor: "hover:bg-pink-300",
		},
		{
			id: "upload",
			label: "Upload Image or Video",
			useImage: true,
			imageSrc: `${IMAGE_PATH}/newsfeed/upload.png`,
			bgColor: "bg-green-200",
			hoverColor: "hover:bg-green-300",
		},
	];

	const options =
		Array.isArray(allowedOptions) && allowedOptions.length > 0
			? allOptions.filter((opt) => allowedOptions.includes(opt.id))
			: allOptions;

	const optionsGrid = (
		<div className="grid grid-cols-2 gap-3 sm:gap-4">
			{options.map((option) => {
				const Icon = option.icon;
				return (
					<button
						key={option.id}
						type="button"
						onClick={() => onSelect(option.id)}
						className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-gray-900 p-4 transition-all ${option.bgColor} ${option.hoverColor}`}
					>
						{option.useImage ? (
							<img
								src={option.imageSrc}
								alt={option.label}
								className="h-10 w-auto"
							/>
						) : (
							<Icon size={40} className="text-gray-900" strokeWidth={2} />
						)}
						<span className="text-center text-sm font-semibold text-gray-900 sm:text-base sm:text-lg">
							{option.label}
						</span>
					</button>
				);
			})}
		</div>
	);

	// darkBackground: parent is a dark panel (e.g. purple blight overlay). Otherwise use high-contrast dark text on white cards (!important beats inherited light colors).
	const inlineTitleClass = darkBackground
		? "mb-4 text-center text-lg font-semibold text-purple-100"
		: "mb-4 text-center text-lg font-semibold !text-neutral-900 dark:!text-neutral-100";

	if (variant === "inline") {
		return (
			<div className="relative">
				<p className={inlineTitleClass}>{displayTitle}</p>
				{optionsGrid}
			</div>
		);
	}

	return createPortal(
		<div
			className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm"
			style={{ zIndex: 100000, pointerEvents: "auto" }}
			onClick={(e) => {
				if (allowClose && e.target === e.currentTarget) {
					onClose?.();
				}
			}}
		>
			<div
				className="relative w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl"
				style={{ pointerEvents: "auto" }}
				onClick={(e) => e.stopPropagation()}
			>
				{allowClose ? (
					<Button
						onClick={onClose}
						size="icon"
						variant="ghost"
						className="absolute right-4 top-4 h-10 w-10 rounded-full bg-red-500 text-white hover:bg-red-600"
					>
						<X size={20} />
					</Button>
				) : null}

				<h2
					className={
						customTitle !== null && customTitle !== undefined
							? "mb-8 text-center text-2xl font-bold text-gray-800 dark:text-gray-200 sm:text-3xl"
							: `mb-8 text-center text-3xl font-bold sm:text-4xl ${titleColors.textColor}`
					}
					style={
						customTitle === null || customTitle === undefined
							? {
									WebkitTextStroke: `0.75px ${titleColors.strokeColor}`,
									textStroke: `0.75px ${titleColors.strokeColor}`,
								}
							: undefined
					}
				>
					{displayTitle}
				</h2>

				{optionsGrid}
			</div>
		</div>,
		document.body
	);
};

export default CaptureMethodSelector;
